import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subject } from 'rxjs';
import { GoogleRecaptcha } from './google-recaptcha.namespace';

@Injectable({
    providedIn: 'root'
})
export class GoogleRecaptchaService {
    private badgeIsDisplayed = false;

    private badgeOpeningObjects: any[] = [];

    private loaded = false;

    private loadedSubject = new Subject<void>();

    private loading = false;

    protected app = inject(App);
    protected timeoutService = inject(TimeoutService);
    protected document = inject(DOCUMENT);

    load(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined') {
                reject();
            }
            const apiOptions = this.apiOptions;
            if (apiOptions) {
                if (this.loaded) {
                    resolve();
                } else if (this.loading || this.isAvailable) {
                    let timeoutId;

                    const onReadySubscription = this.loadedSubject.subscribe(() => {
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                        }
                        onReadySubscription.unsubscribe();
                        resolve();
                    });

                    timeoutId = this.timeoutService.setTimeout(() => {
                        onReadySubscription?.unsubscribe();
                        reject();
                    }, 10000);

                    if (!this.loading) {
                        this.loading = true;
                        window[apiOptions.onload] = () => this.onReady();
                        this.app.request.loadScript(GoogleRecaptcha.API_JS_URL + '?' + this.app.request.serialize(<any>apiOptions));
                    }
                } else {
                    reject();
                }
            } else {
                reject('Google recaptcha key is missing');
            }
        });
    }

    get grecaptcha(): GoogleRecaptcha.IApi | undefined {
        if (typeof window !== 'undefined') {
            return window.grecaptcha;
        }
        return undefined;
    }

    execute(action = 'login'): Promise<string> {
        return new Promise((resolve, reject) => {
            if (this.grecaptcha && this.key) {
                this.grecaptcha.ready(() => {
                    this.grecaptcha
                        .execute(this.key, { action })
                        .then(token => {
                            resolve(token);
                        })
                        .catch(reject);
                });
            } else {
                reject();
            }
        });
    }

    private onReady(): void {
        this.loading = false;
        this.loaded = true;
        this.loadedSubject.next();
    }

    private get isAvailable(): boolean {
        return this.app.isPlatformBrowser;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Configuration
    ////////
    ////////////////////////////////////////////

    private get apiOptions(): GoogleRecaptcha.IApiOptions | undefined {
        const key = this.key;
        if (key) {
            return { onload: GoogleRecaptcha.API_CALLBACK, render: key };
        }
        return undefined;
    }

    protected get key(): string | undefined {
        return this.app.dfConfig.grecaptcha?.key;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Badge
    ////////
    ////////////////////////////////////////////

    displayBadge(badgeOpeningObject: any): void {
        if (typeof this.getBadgeOpeningObjectIndex(badgeOpeningObject) === 'undefined') {
            this.badgeOpeningObjects.push(badgeOpeningObject);
        }
        if (!this.badgeIsDisplayed) {
            this.updateBadgeVisibility(true);
        }
    }

    hideBadge(badgeOpeningObject: any): void {
        const index = this.getBadgeOpeningObjectIndex(badgeOpeningObject);
        if (typeof index === 'number') {
            this.badgeOpeningObjects.splice(index, 1);
        }

        if (this.badgeOpeningObjects.length === 0) {
            this.updateBadgeVisibility(false);
        }
    }

    private updateBadgeVisibility(visible: boolean): void {
        const badgeElement = this.document.querySelectorAll<HTMLElement>('.grecaptcha-badge');
        if (badgeElement?.length) {
            const value = visible ? 'block' : 'none';
            badgeElement.forEach(element => {
                element.style.display = value;
            });
            this.badgeIsDisplayed = visible;
        }
    }

    private getBadgeOpeningObjectIndex(badgeOpeningObject: any): number | undefined {
        const index = this.badgeOpeningObjects.indexOf(badgeOpeningObject);
        if (index > -1) {
            return index;
        }
        return undefined;
    }
}
